<template>
  <div class="relative w-full h-full">
    <EditorDeckSlidesEditorOverlay :show-content="showImageReplace">
      <template #top-bar>
        <div class="flex items-center justify-between">
          <div class="max-w-md">
            <p v-if="slide.file" class="mb-0 truncate">
              Source name:&nbsp;
              <VLink class="text-copper-300" component="a" font-weight="normal" :href="slide.file.url" target="_blank">
                {{ slide.file.name }}
              </VLink>
            </p>
          </div>
          <VButton
            v-if="!showImageReplace"
            ref="replaceImageButtonRef"
            class="flex items-center"
            size="xs"
            @click="$emit('update:showImageReplace', true)"
          >
            <ArrowRotate class="w-3 mr-2" />
            Replace Image
          </VButton>
          <button
            v-else
            class="p-0 bg-transparent border-0 appearance-none close-btn transition-opacity hover_opacity-80"
            :style="{ paddingTop: '5px', paddingBottom: '5px' }"
            type="button"
            @click="$emit('update:showImageReplace', false)"
          >
            <XMarkIcon class="w-6" />
          </button>
        </div>
      </template>
      <template #content>
        <DeckImageSlide :slide="(slide as unknown as SlideImage)" />
      </template>
      <template #overlay>
        <div class="h-full grid place-items-center">
          <EditorLocalOrServerChoice
            ref="localOrServerChoiceContainerRef"
            :company-id="companyId"
            :folder-id="folderId"
            :max-resolution="[4096, 4096]"
            :is-open="showImageReplace"
            file-type="image"
            mime-type="image/jpeg, image/png, image/gif, image/webp"
            @choose-file="chooseFiles($event)"
            @goto:shelf="showShelf = true"
            @error="$emit('error', $event)"
          />
        </div>
      </template>
    </EditorDeckSlidesEditorOverlay>
    <EditorDeckModalsShelfSelector
      type="images"
      :folder-id="folderId"
      :company-id="companyId"
      :showing="showShelf"
      action="update"
      file-type="image"
      mime-type="image/jpeg, image/png, image/gif, image/webp"
      @choose-file="chooseFiles($event)"
      @close="showShelf = false"
    >
      <template #footer><span /></template>
    </EditorDeckModalsShelfSelector>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { ref, onUnmounted, computed } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

import type { Slide, SlideImage } from '@/entities/deck'
import type { ShelfFile } from '@/entities/shelfFile'
import ArrowRotate from '@/components/icons/ArrowRotate.svg'

const props = defineProps({
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  folderId: {
    type: Number,
    default: null,
  },
  companyId: {
    type: Number,
    required: true,
  },
  showImageReplace: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['update:showImageReplace', 'update:slide', 'error'])

const showShelf = ref(false)
const selectedFile = ref<ShelfFile | null>(null)
const localOrServerChoiceContainerRef = ref<any>(null)
const replaceImageButtonRef = ref<any>(null)

useClickOutside(
  [computed(() => replaceImageButtonRef.value?.$el), computed(() => localOrServerChoiceContainerRef.value?.$el)],
  showShelf,
  close
)

onUnmounted(() => {
  close()
})

function chooseFiles(file: ShelfFile) {
  emit('update:slide', {
    id: props.slide.id,
    file_id: file.id,
  })

  close()
}

function close() {
  showShelf.value = false
  selectedFile.value = null
  emit('update:showImageReplace', false)
}
</script>
